import type { ModuleReferenceInfo } from './module-reference'
import { ModuleTypeEnum } from './module'

export enum DocumentTypeEnum {
  FILE = 'FILE',
  URL = 'URL',
}

export type DocumentMimeType =
  | 'IMAGE'
  | 'DOCUMENT'
  | 'VIDEO'
  | 'AUDIO'
  | 'OTHER'

/**
 * Interface for link preview API response
 */
export interface LinkPreviewResponse {
  url: string
  title?: string
  siteName?: string
  description?: string
  mediaType?: string
  contentType?: string
  images?: string[]
  videos?: {
    url: string
    secureUrl: string
    type: string
    width: string
    height: string
  }[]
  favicons?: string[]
}

/**
 * Type alias for PreviewData, using UrlPreviewData
 */
export type PreviewData = UrlPreviewData

/**
 * Metadata for document storage
 */
export interface DocumentMetadata {
  author?: string
  createdDate?: string
  modifiedDate?: string
  pageCount?: number
  wordCount?: number
  thumbnailGenerated?: boolean
  originalFilename?: string
  [key: string]: unknown
}

export interface UrlPreviewData {
  title?: string | unknown
  description?: string | unknown
  image?: string | unknown
  siteName?: string | unknown
  url: string | unknown
  [key: string]: unknown
}

/**
 * Converts a raw link preview response to our standardized UrlPreviewData format
 */
export function convertToUrlPreviewData(
  response: LinkPreviewResponse,
): UrlPreviewData {
  return {
    title: response.title || null,
    description: response.description || null,
    image: response.images?.[0] || null,
    siteName: response.siteName || null,
    url: response.url,
  }
}

export interface Document {
  id: string
  organizationId: string
  categoryId?: string
  type: DocumentTypeEnum
  displayName: string
  description?: string

  // File-specific fields
  fileName?: string
  contentType?: string
  fileSize?: number
  storageKey?: string
  thumbnailKey?: string

  // URL-specific fields
  url?: string
  urlPreviewData?: UrlPreviewData
  lastUrlCheck?: Date
  isUrlValid?: boolean

  // Common fields
  uploadedBy?: string
  moduleType: string
  moduleId: string
  order: number
  isPinned: boolean
  isArchived: boolean
  isProtected: boolean
  metadata?: Record<string, unknown>
  tags: string[]
  documentDate?: Date
  createdAt: Date
  updatedAt: Date
  deletedAt?: Date
}

// Internal type used by the service layer that ensures date fields are Date objects
export type InternalDocument = Omit<Document, 'metadata'> & {
  metadata?: Record<string, unknown> // Use Record<string, unknown> for JSON fields
  documentDate?: Date
}

export interface DocumentCategory {
  id: string
  organizationId: string
  moduleType?: string
  moduleId?: string
  parentId?: string
  name: string
  description?: string
  icon?: string
  color?: string
  order: number
  isProtected: boolean
  createdAt: Date
  updatedAt: Date
  deletedAt?: Date
}

export interface DocumentAttachment {
  id: string
  documentId: string
  organizationId: string
  moduleType: string
  moduleId: string
  attachedBy?: string
  createdAt: Date
}

export interface DocumentAttachmentResponse {
  id: string
  documentId: string
  moduleType: string
  moduleId: string
  attachedBy?: string
  createdAt: string
  document?: DocumentResponse
}

export interface AttachDocumentsDTO {
  documentIds: string[]
  moduleType: string
  moduleId: string
}

export interface DetachDocumentDTO {
  documentId: string
  moduleType: string
  moduleId: string
}

export interface CreateDocumentDTO {
  file?: File | Blob
  url?: string
  displayName: string
  description?: string
  moduleType: ModuleTypeEnum
  moduleId: string
  organizationId: string
  categoryId?: string
  tags?: string[]
  metadata?: Record<string, unknown>
  documentDate?: Date
}

export interface DocumentResponse {
  id: string
  displayName: string
  type: DocumentTypeEnum
  storageKey?: string
  thumbnailKey?: string
  url?: string
  fileName?: string
  contentType?: string
  fileSize?: number
  metadata?: Record<string, unknown>
  tags: string[]
  createdAt: string
  isPinned: boolean
  moduleType: string
  moduleId: string
  categoryId?: string
  documentDate?: string
  moduleInfo?: ModuleReferenceInfo | null
  uploadedBy?: string
  isProtected?: boolean
  customFields?: Record<string, unknown>
  [key: `custom_${string}`]: unknown
}

// =====================
// A record's Files table, paged (GET /documents/by-module/:type/:id with page/limit)
// =====================

/**
 * One row of a record's Files table: a file the record owns, or one attached
 * to it from elsewhere. Owned wins when a file is both.
 */
export interface ModuleDocumentResponse extends DocumentResponse {
  isAttachment: boolean
  /** The attachment link's id, for files attached from elsewhere. */
  attachmentId?: string
}

/**
 * A record's Files table loads up to this many files in one request and
 * works on them in the browser; above it, the table pages, sorts, searches
 * and filters folders on the server. Also the API's page-size cap.
 */
export const MODULE_DOCUMENTS_CLIENT_SIDE_LIMIT = 500

/** Server-sortable columns of a record's Files table. */
export const MODULE_DOCUMENT_SORT_FIELDS = [
  'displayName',
  'createdAt',
  'documentDate',
  'type',
  'fileSize',
  'isPinned',
] as const

export type ModuleDocumentSortField =
  (typeof MODULE_DOCUMENT_SORT_FIELDS)[number]

export interface ModuleDocumentsQuery {
  page?: number
  limit?: number
  search?: string
  /** Only files in these folders (the client expands subfolders). */
  categoryIds?: string[]
  sortBy?: ModuleDocumentSortField
  sortOrder?: 'asc' | 'desc'
}

/**
 * Counts for a record's Files table, independent of the loaded page — what
 * the folder chips and the "Files N" header read.
 */
export interface ModuleDocumentsSummary {
  /** Owned plus attached files the viewer can see. */
  total: number
  attachedCount: number
  /** Files directly in each folder (subfolders not rolled up). */
  byCategory: Record<string, number>
  /** Files in no folder. */
  uncategorized: number
}

// =====================
// Nested files (documents owned by records further down a record's chain)
// =====================

/** Record kinds whose Files space can reach down the chain. */
export const NESTED_DOCUMENT_ROOT_TYPES = [
  ModuleTypeEnum.ENTITY,
  ModuleTypeEnum.PROJECT,
  ModuleTypeEnum.TASK,
] as const

export type NestedDocumentRootType = (typeof NESTED_DOCUMENT_ROOT_TYPES)[number]

/** One hop between the root record and the record that owns a file. */
export interface DocumentLineageNode {
  moduleType: string
  moduleId: string
  name: string
  /** Short human identifier, e.g. a task number or project code. */
  reference?: string
}

/** A descendant record that owns at least one matching file. */
export interface NestedDocumentSource {
  moduleType: string
  moduleId: string
  /**
   * Path from the first record below the root down to (and including) this
   * record. Records the viewer can't read are left out of the path.
   */
  lineage: DocumentLineageNode[]
  documentCount: number
}

export interface NestedDocumentResponse extends DocumentResponse {
  lineage: DocumentLineageNode[]
  /** The folder the file sits in on its own record, when it is in one. */
  categoryName?: string
}

export interface NestedDocumentsResponse {
  documents: NestedDocumentResponse[]
  /** All matching files, including any beyond this page. */
  total: number
  sources: NestedDocumentSource[]
}

export interface NestedDocumentsQuery {
  search?: string
  /** `0` returns only `total` and `sources` — the outline, no documents. */
  limit?: number
  offset?: number
  /** `TYPE:id` of one descendant record, to page through just its files. */
  source?: string
}

// =====================
// Document protection (access-restricted folders & files)
// =====================

export interface DocumentAccessMemberUser {
  id: string
  firstName?: string | null
  lastName?: string | null
  displayName?: string | null
  email?: string
  image?: string | null
}

export interface DocumentAccessMemberResponse {
  id: string
  userId: string
  user?: DocumentAccessMemberUser
  addedBy?: string
  createdAt: string
}

export interface DocumentProtectionResponse {
  isProtected: boolean
  members: DocumentAccessMemberResponse[]
  // Set when a protected ancestor folder also restricts this item
  inheritedFrom?: {
    categoryId: string
    name: string
  }
}

export interface UpdateDocumentProtectionDTO {
  isProtected: boolean
  userIds?: string[]
}

export interface UpdateDocumentAccessListDTO {
  userIds: string[]
}

// Constants for file size limits
export const FILE_SIZE_LIMITS: Record<DocumentMimeType, number> = {
  IMAGE: 10 * 1024 * 1024, // 10MB
  DOCUMENT: 50 * 1024 * 1024, // 50MB
  VIDEO: 100 * 1024 * 1024, // 100MB
  AUDIO: 50 * 1024 * 1024, // 50MB
  OTHER: 10 * 1024 * 1024, // 10MB
} as const

// Constants for allowed mime types
export const ALLOWED_MIME_TYPES: Record<
  Exclude<DocumentMimeType, 'OTHER'>,
  readonly string[]
> = {
  IMAGE: [
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'image/svg+xml',
    'image/tiff',
    'image/bmp',
  ],
  DOCUMENT: [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/postscript',
    'application/illustrator',
    'application/x-illustrator',
    'application/rtf',
    'application/zip',
    'text/plain',
    'text/csv',
    'text/html',
    'text/javascript',
    'text/css',
    'application/json',
    'application/xml',
    'application/octet-stream',
  ],
  VIDEO: [
    'video/mp4',
    'video/quicktime',
    'video/x-msvideo',
    'video/mpeg',
    'video/webm',
    'video/ogg',
    'video/3gpp',
  ],
  AUDIO: [
    'audio/mpeg',
    'audio/wav',
    'audio/ogg',
    'audio/mp3',
    'audio/mp4',
    'audio/aac',
    'audio/webm',
  ],
} as const

// Thumbnail sizes
export const THUMBNAIL_SIZES = {
  SMALL: { width: 150, height: 150 },
  MEDIUM: { width: 300, height: 300 },
  LARGE: { width: 600, height: 600 },
} as const

// Helper function to determine document type from mime type
export function getDocumentType(mimeType: string): DocumentMimeType {
  if (mimeType.startsWith('image/')) {
    return 'IMAGE'
  } else if (mimeType.startsWith('video/')) {
    return 'VIDEO'
  } else if (mimeType.startsWith('audio/')) {
    return 'AUDIO'
  } else if (
    mimeType.startsWith('application/') ||
    mimeType.startsWith('text/')
  ) {
    return 'DOCUMENT'
  }
  return 'OTHER'
}

// Flag to control mime type enforcement (set to false to allow all types)
export const ENFORCE_MIME_TYPE_RESTRICTIONS = false
